import React from "react"
import {useTheme} from "@material-ui/core"
import {useTranslation} from "react-i18next"

// Components
import Sparkline from "../../../../../components/modern/Sparkline"

// Models
import {DoctorStatByPeriod, DoctorStatsType} from "../../../models"

// Types
import {TabKey} from "../ModernRecordsCard/columns"

// Utils
import {sparkFromValue} from "../../../../../utils/ModernFormat"

// Styles
import useStyles from "./styles"
import {ModernTheme} from "../../../../../theme/modern/tokens"

type StatCardDef = {
    period: TabKey
    labelKey: string
    pick(stats: DoctorStatsType): DoctorStatByPeriod
    tone: "accent" | "ok" | "warn" | "ink3"
    footKey?: string
}

const CARDS: Array<StatCardDef> = [
    {period: "today", labelKey: "modern.reviewed_today", pick: s => s.day, tone: "accent"},
    {period: "week", labelKey: "modern.this_week", pick: s => s.week, tone: "ok", footKey: "modern.foot_week"},
    {period: "month", labelKey: "modern.this_month", pick: s => s.month, tone: "warn", footKey: "modern.foot_month"},
    {period: "all", labelKey: "modern.all_time", pick: s => s.alltime, tone: "ink3", footKey: "modern.foot_all"},
];

type ModernStatCardsProps = {
    stats: DoctorStatsType | null
    active: TabKey
    onSelect(period: TabKey): void
}

/**
 * Four review-activity cards that double as period filters for the records
 * table below them. Below 640px only "Reviewed today" is rendered — see
 * `stat__minor` in the stylesheet.
 *
 * Rendered to match the approved design exactly, which means two elements here
 * are PRESENTATIONAL and carry no data:
 *
 * - The "▲" chip is a static design accent, not a computed trend. There is no
 *   period-over-period figure in the stats payload to derive one from, so it is
 *   marked aria-hidden and must never be read as a real direction of travel.
 * - The card foot captions ("this week", "this month", …) are fixed strings
 *   from the design, restating the card's own period. The design's fourth
 *   caption, "goal 30", is gone: it read as a review target the reviewer was
 *   being held to, and nothing in the product sets, stores or tracks one — no
 *   goal field exists on `DoctorStatByPeriod`.
 *
 * The sparkline is decorative for the same reason — see `sparkFromValue` — and
 * is aria-hidden inside <Sparkline/>.
 *
 * If any of these three ever needs to mean something, it must be driven by a
 * real field; do not retrofit a calculation onto the numbers already here.
 *
 * @param props
 */
export default function ModernStatCards(props: ModernStatCardsProps) {
    const styles = useStyles();
    const theme = useTheme<ModernTheme>();
    const [t] = useTranslation("common");
    const {stats, active, onSelect} = props;

    // Resolved here rather than in the stylesheet because <Sparkline/> takes a
    // colour value, not a class. Nothing else on the card is tinted any more:
    // the accent bar along the bottom edge and the accent ring on the selected
    // card were four coloured glows carrying no information, and are gone.
    const tones: {[key: string]: string} = {
        accent: theme.modern.accent,
        ok: theme.modern.ok,
        warn: theme.modern.warn,
        ink3: theme.modern.ink3,
    };

    return (
      <section className={styles.stats} aria-label={t("stats.title")}>
          {CARDS.map(card => {
              const bucket = stats === null ? null : card.pick(stats);
              const reviewed = bucket === null ? null : bucket.reviewed;
              const skipped = bucket === null ? null : bucket.skipped;
              const accent = tones[card.tone];
              const isActive = active === card.period;

              return (
                <button
                  key={card.period}
                  type="button"
                  className={[
                      styles.stat,
                      isActive ? styles.stat__active : "",
                      card.period === "today" ? "" : styles.stat__minor,
                  ].filter(Boolean).join(" ")}
                  aria-pressed={isActive}
                  onClick={() => onSelect(isActive ? "all" : card.period)}
                >
                    <span className={styles.stat_top}>
                        <span className={styles.microlabel}>{t(card.labelKey)}</span>
                        {/* static design accent — not a computed trend, see the note above */}
                        <span className={styles.delta} aria-hidden="true">▲</span>
                    </span>

                    <span className={styles.stat_num}>
                        {reviewed === null ? "—" : reviewed.toLocaleString()}
                    </span>

                    <span className={styles.stat_foot}>
                        <span className={styles.stat_foot_label}>
                            {card.footKey ? t(card.footKey) : ""}
                        </span>
                        {/*
                          * Suppressed at zero on purpose. sparkFromValue(0) is
                          * seven zeros, which draws a dead flat rule that reads
                          * as a broken chart rather than as "no activity yet".
                          * The number above already says 0.
                          */}
                        {reviewed !== null && reviewed > 0 && (
                          <Sparkline data={sparkFromValue(reviewed)} color={accent}/>
                        )}
                    </span>
                </button>
              )
          })}
      </section>
    )
}
